const ACTIVITY_TYPES = [
   "assessment",
   "board",
   "comments",
   "diagnostic_module",
   "e_reader",
   "flashcards",
   "html",
   "iframe",
   "lesson",
   "lti",
   "page",
   "pdf",
   "picture",
   "project",
   "video",
   "custom",
   "audio",
   "plugin_component"
] as const
export type ActivityType = typeof ACTIVITY_TYPES[number];

const ACTIVITY_TYPE_LABELS: Record<ActivityType, string> = {
  assessment: "Assessment",
  board: "Discussion Board",
  lti: "LTI Link",
  custom: "Custom Column",
  page: "Page",
  lesson: window?.labelBundle?.["lesson"] || "Lesson",
  html: "HTML",
  video: "Video",
  e_reader: "E-Reader",
  iframe: "Embedded URL",
  audio: "Audio",
  pdf: "PDF",
  picture: "Image",
  project: "Project",
  comments: "Comments",
  flashcards: "Flashcards",
  diagnostic_module: "Diagnostic Module",
  plugin_component: "Activity",
};


export interface Activity {
  metaid?: string; // used for custom activities to link them to their source activity
  id: string,
  title?: string,
  instructor_title?: string,
  link?: string,
  type: ActivityType,
  sub_type?: string,
  graded: boolean,
  /** If true then that means the activity is non-interactive content (pages, lessons, videos, PDFs, etc.) that doesn't produce scores. */
  course_data_only?: boolean,
  /** user id of component owner */
  owned_by?: string,
  lessonName?: string,
  lesson?: {
    id: string,
    title: string
  },
  index?: number,
  report_title?: string,
  thumbnailUrl?: string,
  max_score?: number;
  display_due_date?: string,
  open_date?: string, // iso string in UTC
  close_date?: string, // iso string in UTC
  due_date?: string, // iso string in UTC
  submissions?: number,
  scoring_type?: string,
  count_graded?: number,
  count_overdue?: number,
  suppress_until_available?: boolean,
  suppress_until_available_for_specific_types?: boolean, // derived property, not from backend
  activity_id?: string,
  items?: any,
  // ... and probably more optionals that I missed
  submission_attempts?: number;
  resume_max_attempts?: number;
  show_score?: ShowScore;
  show?: boolean;
  show_correct_answers?: ShowAnswers;
  show_responses?: ShowAnswers;
  inline?: ShowInline;
  show_distractor_rationale_review?: boolean;
  accent_scoring?: number;
  scramble?: boolean;
  lock_back_button?: boolean;
  time_limit?: number;
  requires_comment?: boolean;
  confidence?: boolean;
  studycenter?: boolean;
  policy?: Policy;
  preset_id?: string;
  pickx?: number;
  rubric_id?: string;
  lti_version?: "1.1" | "1.3";
  scoring_method?: string;
  //
  oopen_date?: string; // MM/dd/yyyy shifted to timezone selected by user (local by default)
  ddue_date?: string; // MM/dd/yyyy shifted to timezone selected by user (local by default)
  cclose_date?: string; // MM/dd/yyyy shifted to timezone selected by user (local by default)
  open_time?: string; // HH:mm shifted to timezone selected by user (local by default)
  due_time?: string; // HH:mm shifted to timezone selected by user (local by default)
  close_time?: string; // HH:mm shifted to timezone selected by user (local by default)
  //
  in_advanced?: string; // element is tab in advanced element
  cd_sua?: boolean; // course data suppress until available
  comments_comp?: string;
  actual?: string,
  rollup_id?: string;
  display_type?: string;
  open_date_in_future?: string;
  dtitle?: string;
  dlink?: string;
  locked?: boolean;
  ttype?: string;
  ttime?: unknown;
  resp_avail?: unknown;
  sscoring_type?: string;
  ssubmission_attempts?: string | number;
  resume_attempts?: number;
  ignore?: boolean;
  draft?: boolean;
  selected?: boolean;
  preset_name?: string;
}

export type ActivityWithGradeStats = Activity & { submissions: number; count_graded: number; count_overdue: number };
export type ActivityWithStudentDateOverrides = Activity & Partial<{ student_due: string; student_open: string; student_close: string; }>;

export const ActivityModel = {
  types: (): Array<ActivityType> => [
    "assessment",
    "board",
    "comments",
    "custom",
    "diagnostic_module",
    "e_reader",
    "flashcards",
    "html",
    "iframe",
    "lesson",
    "lti",
    "page",
    "pdf",
    "picture",
    "plugin_component",
    "project",
    "video",
  ],

  displayType: (type: ActivityType): string => ACTIVITY_TYPE_LABELS[type] || type,

  getTypeLabel: (activity: Activity): string =>
    ACTIVITY_TYPE_LABELS[activity.type] || activity.type,

  hasDateInput: {
    open: (activity: Activity) => ["assessment", "board", "lesson", "page", "plugin_component"].includes(activity.type),
    close: (activity: Activity) => ["assessment", "board", "comments", "plugin_component"].includes(activity.type),
    due: (activity: Activity) => [
        "assessment",
        "board",
        "comments",
        "course",
        "diagnostic_module",
        "e_reader",
        "flashcards",
        "html",
        "iframe",
        "lesson",
        "lti",
        "page",
        "pdf",
        "picture",
        "plugin_component",
        "project",
        "video",
      ].includes(activity.type),
  },

  getDisplayLink: (activity: Activity, allActivities: Activity[], index?: number): string => {
    if (activity.type === "comments") {
      const i = index ?? allActivities.indexOf(activity);
      return activity.link + "/" + allActivities.at(i - 1)?.id + "?view=comments";
    }
    return activity.link + "/" + activity.id;
  },

  formatScoringType: (activity: Activity): string => {
    if (activity.course_data_only) return '';
    if (!activity.scoring_type) return 'Manual';
    if (activity.scoring_type === '--') return '';
    return activity.scoring_type;
  },

  getSubmissionAttemptsDisplay: (activity: Activity): string | number =>
    activity.type === "assessment" ? activity.submission_attempts || "∞" : "",

  getTimeLimit: (activity: Activity): string | undefined => {
    if (activity.type !== "assessment") return undefined;
    const secs = activity.time_limit;
    if (!secs || secs == 0) return "Not Timed";
    const TIME_LABELS = {
      900: "15 mins", 1800: "30 mins", 2700: "45 mins", 3600: "1 hour",
      4500: "75 mins", 5400: "90 mins", 6300: "105 mins", 7200: "2 hours",
    };
    return TIME_LABELS[secs] || Math.round(secs / 60) + " mins";
  },

  getResponsesAvailable: (activity: Activity): string | undefined => {
    if (activity.type !== "assessment") return undefined;

    const RESPONSES_MAP = {
      [ShowAnswers.IMMEDIATELY]: {
        [ShowAnswers.IMMEDIATELY]: "Answers on Submit",
        [ShowAnswers.AFTER_DUE_DATE]: "Review on Submit, Answers after Due Date",
        [ShowAnswers.AFTER_CLOSE_DATE]: "Review on Submit, Answers after Close Date",
        [ShowAnswers.AFTER_MAX_ATTEMPTS]: "Review on Submit, Answers after Max Attempts",
        _default: "Review on Submit",
      },
      [ShowAnswers.AFTER_DUE_DATE]: {
        [ShowAnswers.AFTER_DUE_DATE]: "Answers after Due Date",
        [ShowAnswers.AFTER_CLOSE_DATE]: "Review after Due Date, Answers after Close Date",
        [ShowAnswers.AFTER_MAX_ATTEMPTS]: "Review after Due Date, Answers after Max Attempts",
        _default: "Review after Due Date",
      },
      [ShowAnswers.AFTER_CLOSE_DATE]: {
        [ShowAnswers.AFTER_CLOSE_DATE]: "Answers after Close Date",
        _default: "Review after Close Date",
      },
      [ShowAnswers.AFTER_MAX_ATTEMPTS]: {
        [ShowAnswers.AFTER_MAX_ATTEMPTS]: "Answers after Max Attempts",
        [ShowAnswers.AFTER_CLOSE_DATE]: "Review after Max Attempts, Answers after Close Date",
        _default: "Review after Max Attempts",
      },
    };

    const INLINE_MAP = {
      [ShowInline.IMMEDIATELY]: "Review Inline",
      [ShowInline.IMMEDIATELY_WITH_CORRECT_ANSWERS]: "Answers Inline",
      [ShowInline.IMMEDIATELY_WITH_CORRECT_ANSWERS_AFTER_DUE_DATE]: "Review Inline, Answers after Due Date",
      [ShowInline.AFTER_DUE_DATE]: "Review Inline after Due Date",
      [ShowInline.AFTER_DUE_DATE_WITH_CORRECT_ANSWERS]: "Answers Inline after Due Date",
    };

    const showResponses = activity.show_responses;
    const showCorrectAnswers = activity.show_correct_answers;
    const inline = activity.inline;

    if (showResponses && RESPONSES_MAP[showResponses]) {
      const map = RESPONSES_MAP[showResponses];
      return map[showCorrectAnswers] || map._default;
    }

    return INLINE_MAP[inline] || "Answers not shown";
  },
};

export interface ScoreRecord {
  score: number,
  _id?: string,
  session_id?: string,  // learnosity session
  course_id?: string,
  activity_id?: string,
  user_id?: string,
  max_score?: number,
  dt_completed?: string, // date scored
  dt_started?: string, // date created
  submitted_at?: string,
  overdue_accepted?: boolean | number,
  responses?: Array<object>,
  custom?: boolean,
  contains_overdue?: boolean | number,
  needs_grading?: boolean,
  manually_rejected?: boolean
  manually_accepted?: boolean
  submitted_score?: number,
  not_displaying_score?: boolean,
  not_displaying_score_yet?: boolean,
  instructor_graded_this?: boolean,
  project_reopened?: boolean,
}

export interface ActivityStudentScore {
  [activity_id: string]: {
    [student_id: string]: ScoreRecord // Last key-value is always average activity score `average: number`
  }
}

export interface AssignmentObject {
  title: string;
  start: string;
  end: string;
  url?: string;
  extendedProps?: {
      type: string;
      assignedDate: string;
      submitted: boolean | string;
      submittedCount: string;
      graded: string;
      gradedCount: string;
      averageGrade: string;
      status: string;
      done: boolean;
      assignmentUrl: string;
  }
}

export enum ShowInline {
  AFTER_DUE_DATE = "After Due Date",
  IMMEDIATELY = "Immediately",
  IMMEDIATELY_WITH_CORRECT_ANSWERS = "Correct Answers Immediately",
  IMMEDIATELY_WITH_CORRECT_ANSWERS_AFTER_DUE_DATE = "Immediately Correct Answers After Due Date",
  AFTER_DUE_DATE_WITH_CORRECT_ANSWERS = "Correct Answers After Due Date",
  NEVER = "Never",
}

export enum HumanlyChoices {
  HOUR = "Hour",
  DAY = "Day",
  WEEK = "Week",
}
export type DefinedPolicy = {
  max: number;
  penalty: number;
  per: number;
  humanly_per?: HumanlyChoices;
}
export type Policy = DefinedPolicy

export const Policy = {
  isNotEmpty: (policy: Policy): policy is DefinedPolicy => Object.keys(policy).length > 0
}

export enum ShowAnswers {
  NEVER = "Never",
  AFTER_DUE_DATE = "After Due Date",
  AFTER_CLOSE_DATE = "After Close Date",
  AFTER_MAX_ATTEMPTS = "After Max Attempts",
  IMMEDIATELY = "Immediately",
}

export enum ShowScore {
  IMMEDIATELY = "Immediately",
  WITH_RESPONSES = "When responses are available",
}

export enum GradedState {
  GRADED = "Graded",
  NOT_GRADED = "Not Graded",
}

export type ActivityLockStatus = 'locked' | 'unlocked' | 'locked-disabled' | 'none';

export interface LastSubmittedActivityPerStudent {
  [student_id: string]: {
    submission_date: string,
    activity_id: string
  }
}

export interface LessonAssessments {
  [lesson_id: string]: {
    activities: Activity[],
    distribution: any, // TODO not sure where this is used but nice to have it typed
    scores: {
      [activity_id: string]: {
        [student_id: string]: number  // score, not percentage
      }
    }
  }
}
